import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  THEMES, applyTheme, isTheme, readChoice, resolveTheme, saveChoice, type Theme,
} from '../lib/theme.js';
import { createPortal } from 'react-dom';
import { initialOf, type ViewerState } from '../lib/viewer.js';
import { auth } from '../lib/tomo.js';
import { BugReportModal } from './BugReportModal.js';
import { PreferencesModal } from './PreferencesModal.js';

/**
 * Everything about "you", behind your own face.
 *
 * The same control the room has, for the same reason: the avatar is already
 * the thing that says who you are, and it is the thing people reach for to
 * find account settings. A row of a face, a name and two buttons spends
 * permanent width on things done roughly never.
 *
 * SHOWN SIGNED OUT TOO, as a neutral face. The appearance setting lives in
 * here, and somebody who has not signed in still has eyes — hiding the menu
 * from them would put the theme behind an account.
 *
 * Closes on outside click and on Escape. Both, because either alone is the
 * kind of half-finished menu that leaves a panel stranded over the page.
 */
export function AccountMenu({ viewer }: { readonly viewer: ViewerState }): ReactNode {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(() => currentTheme());
  const [dialog, setDialog] = useState<'preferences' | 'report' | null>(null);
  const closeDialog = useCallback(() => { setDialog(null); }, []);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent): void => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    // `pointerdown`, not `click`: a click listener fires after the button's
    // own handler and would reopen the menu the same press just closed.
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const chooseTheme = (next: Theme): void => {
    applyTheme(next, document.documentElement);
    saveChoice(next, window.localStorage);
    setTheme(next);
  };

  const signIn = (): void => {
    void auth.start(window.location.pathname + window.location.search).then(
      (url) => { window.location.assign(url); },
      () => { /* A login that cannot start leaves the page exactly as it was. */ },
    );
  };

  const name = viewer ? (viewer.displayName ?? viewer.handle ?? 'signed in') : 'Account';

  return (
    <div ref={root} className="relative shrink-0">
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); }}
        aria-haspopup="menu"
        aria-expanded={open}
        // The name is announced, not drawn. Without this the control reads as
        // "button" and a screen reader user has no idea whose account it is.
        aria-label={`${name} — account menu`}
        className="block rounded-full outline-offset-2 focus-visible:outline focus-visible:outline-2"
      >
        {viewer?.avatarUrl ? (
          <img
            src={viewer.avatarUrl}
            alt=""
            className={`size-7 rounded-full ring-1 transition ${
              open ? 'ring-brand-400' : 'ring-ink-800 hover:ring-ink-700'
            }`}
          />
        ) : (
          <span
            className={`grid size-7 place-items-center rounded-full bg-ink-800 text-[11px] font-semibold text-ink-400 ring-1 transition ${
              open ? 'ring-brand-400' : 'ring-transparent hover:ring-ink-700'
            }`}
          >
            {viewer ? initialOf(viewer) : <PersonIcon />}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1.5 w-48 overflow-hidden rounded-lg border border-ink-800 bg-ink-900 shadow-lg"
        >
          {viewer ? (
            <p className="truncate border-b border-ink-800 px-3 py-2 text-[13px] font-semibold text-ink-50">
              {name}
            </p>
          ) : null}

          {/*
            The room's menu, row for row, plus the one thing the room does not
            have: appearance. Preferences are the room's own settings — see
            PreferencesModal for why they are the same storage, not a copy.
          */}
          {viewer ? (
            <MenuRow onClick={() => { setOpen(false); setDialog('preferences'); }}>
              Preferences
            </MenuRow>
          ) : null}

          {/*
            The studio, for anybody signed in. It had no link anywhere, so
            the only way in was typing /studio. A link rather than a button,
            so it opens in a new tab like any other place.
          */}
          {viewer ? (
            <a
              role="menuitem"
              href="/studio"
              className="block w-full px-3 py-2 text-left text-[13px] text-ink-400 hover:bg-ink-800 hover:text-ink-50"
            >
              Studio
            </a>
          ) : null}

          {/*
            All three at once, with the current one marked. This was a single
            row that named what pressing it would DO ("Dark mode" while light
            was on), since a row that names its own state gets read as the
            opposite. With three themes, one press can't say where it goes,
            so the choices are laid out side by side and the current one is
            shown as pressed.

            Stays open on a pick: seeing the page change behind the menu is
            the reason to try each one.
          */}
          <div role="group" aria-label="Appearance" className="flex gap-1 px-3 py-2">
            {THEMES.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={theme === option}
                onClick={() => { chooseTheme(option); }}
                className={`flex-1 rounded px-1.5 py-1 text-[12px] capitalize transition ${
                  theme === option
                    ? 'bg-ink-50 font-semibold text-ink-950'
                    : 'text-ink-400 hover:bg-ink-800 hover:text-ink-50'
                }`}
              >
                {option}
              </button>
            ))}
          </div>

          {/*
            Above Sign out, never at the bottom: the last row is where people
            expect the destructive one, and this is reached for while already
            frustrated. Signed out too — reports are open to everyone.
          */}
          <MenuRow onClick={() => { setOpen(false); setDialog('report'); }}>
            Report a problem
          </MenuRow>

          {viewer ? (
            <MenuRow
              onClick={() => {
                void auth.signOut().finally(() => { window.location.reload(); });
              }}
            >
              Sign out
            </MenuRow>
          ) : (
            <MenuRow onClick={signIn}>Sign in with Twitch</MenuRow>
          )}
        </div>
      )}

      {/*
        PORTALLED, not rendered in place. This menu sits in headers that use
        `backdrop-blur`, and a backdrop filter makes its element the
        containing block for `fixed` descendants — the modal would be clipped
        to a 56px header instead of covering the page.
      */}
      {dialog === 'preferences' && createPortal(<PreferencesModal onClose={closeDialog} />, document.body)}
      {dialog === 'report' && createPortal(<BugReportModal onClose={closeDialog} />, document.body)}
    </div>
  );
}

function MenuRow({ onClick, children }: {
  readonly onClick: () => void;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="block w-full px-3 py-2 text-left text-[13px] text-ink-400 hover:bg-ink-800 hover:text-ink-50"
    >
      {children}
    </button>
  );
}

function currentTheme(): Theme {
  if (typeof document === 'undefined') return 'dark';
  const attr = document.documentElement.dataset.theme;
  if (isTheme(attr)) return attr;
  return resolveTheme(readChoice(window.localStorage), prefersLight());
}

function prefersLight(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: light)').matches;
  } catch {
    return false;
  }
}

/* Drawn, not typed — an emoji is a font the platform picks, ignores
   `currentColor`, and is read aloud by its Unicode name. */
function PersonIcon(): ReactNode {
  return (
    <svg viewBox="0 0 16 16" aria-hidden className="size-3.5" fill="currentColor">
      <circle cx="8" cy="5" r="3" />
      <path d="M8 9.4c-2.8 0-5 1.7-5 3.8 0 .5.4.8.9.8h8.2c.5 0 .9-.3.9-.8 0-2.1-2.2-3.8-5-3.8Z" />
    </svg>
  );
}
